import React from "react";
import {
  Datagrid,
  TextField,
  Filter,
  TextInput,
  TopToolbar,
  CreateButton,
  sanitizeListRestProps,
  FunctionField,
  Record,
  SelectInput,
  Responsive,
  SimpleList,
} from "react-admin";
import "../../common/styles/list-style.css";
import IndeterminateCheckBoxIcon from "@material-ui/icons/IndeterminateCheckBox";
import CheckBoxIcon from "@material-ui/icons/CheckBox";
import { useSelector } from "react-redux";
import { AppState } from "../../../common/types";
import { useTheme, Theme, Box, Tooltip } from "@material-ui/core";
import { useListContext, useTranslate } from "react-admin";
import { ArrayField } from "../../common/data-grid-fields/ArrayField";
import { ITrailerGridView } from "../../../common/data/data-types/trailers";
import { ActionsField } from "../../common/data-grid-fields/ActionsField";
import { IResourceListProps } from "../../common/ra-types";
import { useSearchFields } from "../../../common/hooks/useSearchFields";
import { ProLogsEmpty } from "../../common/list/ProLogsEmpty";
import TrailerOutlineIcon from "../../../common/icons/TrailerOutlineIcon";
import { DeleteRecordMenuItem } from "../../common/data-grid-fields/DeleteRecordMenuItem";
import permissionNames from "../../../common/auth/access-control/permissionNames";
import { useActionPermissions } from "../../../common/auth/useActionPermissions";
import { useFieldPermissions } from "../../../common/hooks/useFieldPermissions";
import { protectedEntities } from "../../../common/auth/permissions";
import { FieldPermissionsContext } from "../../common/field-permissions/FieldPermissionsContext";
import { ProtectedElement } from "../../common/field-permissions/ProtectedElement";
import { DefaultProLogsList } from "../../common/list/DefaultProLogsList";
import { useResetListSearchField } from "../../../common/hooks/useResetListSearchField";
import { useAppData } from "../../../common/hooks/useAppData";
import EmojiTransportationIcon from "@material-ui/icons/EmojiTransportation";
import Icon from "@mdi/react";
import {
  mdiCalendarMonth,
  mdiCardAccountDetails,
  mdiFileDocumentOutline,
} from "@mdi/js";
import HomeWorkIcon from "@material-ui/icons/HomeWork";
import BlockIcon from "@material-ui/icons/Block";

const ListFilter = (props: any) => {
  useSearchFields(props, ["name", "licensePlate", "vin"]);
  useResetListSearchField(props);
  const { loading } = useListContext();
  const companies = useAppData((x) => x.companies);
  return (
    <Filter {...props}>
      {!loading && (
        <TextInput
          className="Trailers-list-filter search-box"
          label="resources.common.labels.search"
          source="q"
          alwaysOn
          autoFocus
          autoComplete="off"
        />
      )}
      {loading && (
        <TextInput
          className="Trailers-list-filter search-box"
          label="resources.common.labels.search"
          source="q"
          alwaysOn
        />
      )}
      <SelectInput
        className="filter-box"
        label="Filter by trailer status"
        allowEmpty
        source="status"
        alwaysOn
        choices={[
          { id: "Active", name: "Active Trailers" },
          { id: "Inactive", name: "Inactive Trailers" },
        ]}
      />
      <SelectInput
        className="filter-box"
        label="Filter by company"
        allowEmpty
        source="companyId"
        alwaysOn
        choices={companies}
      />
    </Filter>
  );
};

export const TrailersList = (props: IResourceListProps) => {
  const themeName = useSelector((state: AppState) => state.theme);
  const theme: Theme = useTheme();
  const translate = useTranslate();
  const permissions = useActionPermissions();
  const fieldPermissions = useFieldPermissions(
    protectedEntities.Trailer,
    (x) => x.list
  );

  const ListActions = (props: any) => {
    const { className, exporter, filters, maxResults, ...rest } = props;
    const { basePath } = useListContext();
    const permissions = useActionPermissions();
    return (
      <TopToolbar className={className} {...sanitizeListRestProps(rest)}>
        {permissions &&
          permissions[permissionNames.admin.trailers.main] &&
          permissions[permissionNames.admin.trailers.create] && (
            <CreateButton basePath={basePath} />
          )}
      </TopToolbar>
    );
  };

  interface SimpleListProps {
    record?: Record;
  }

  const RenderPrimaryTextForSimpleList = ({ record }: SimpleListProps) => {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          gap: "10px",
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            gap: "6px",
            alignItems: "center",
          }}
        >
          <ProtectedElement source="name">
            <TextField
              source="name"
              label="resources.admin-trailers.labels.name"
            />
          </ProtectedElement>
          {" | "}
          <ProtectedElement source="trailerType">
            <FunctionField<ITrailerGridView & Record>
              source="trailerType"
              label="resources.admin-trailers.labels.type"
              render={(record) =>
                translate(`appData.trailerTypes.${record?.trailerType}`)
              }
            />
          </ProtectedElement>
        </div>

        <FunctionField<ITrailerGridView & Record>
          source="status"
          headerClassName="centered-content"
          label="resources.common.labels.active"
          render={(record) => (
            <Box className="centered-content">
              {record?.status === "Active" ? (
                <CheckBoxIcon
                  className={record.status}
                  style={{
                    color:
                      themeName === "dark"
                        ? theme.palette.success.dark
                        : theme.palette.success.light,
                  }}
                />
              ) : (
                <IndeterminateCheckBoxIcon
                  className={record?.status}
                  style={{
                    color:
                      themeName === "dark"
                        ? theme.palette.error.dark
                        : theme.palette.error.light,
                  }}
                />
              )}
            </Box>
          )}
        />
      </div>
    );
  };

  const RenderSecondaryTextForSimpleList = ({ record }: SimpleListProps) => {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "6px",
          marginTop: "14px",
        }}
      >
        {record && record.make ? (
          <Tooltip title="Make">
            <div
              style={{
                display: "flex",
                gap: "6px",
                alignItems: "center",
              }}
            >
              <EmojiTransportationIcon
                style={{
                  color: theme.palette.success.main,
                }}
              />
              <ProtectedElement source="make">
                <TextField
                  source="make"
                  label="resources.admin-trailers.labels.make"
                />
              </ProtectedElement>
            </div>
          </Tooltip>
        ) : null}
        {record && record.year ? (
          <Tooltip title="Year">
            <div
              style={{
                display: "flex",
                gap: "6px",
                alignItems: "center",
              }}
            >
              <Icon
                style={{
                  color: theme.palette.success.main,
                }}
                path={mdiCalendarMonth}
                size={1}
              />
              <ProtectedElement source="year">
                <TextField
                  source="year"
                  label="resources.admin-trailers.labels.year"
                />
              </ProtectedElement>
            </div>
          </Tooltip>
        ) : null}
        {record && record.licensePlate ? (
          <Tooltip title="License plate">
            <div
              style={{
                display: "flex",
                gap: "6px",
                alignItems: "center",
              }}
            >
              <Icon
                style={{
                  color: theme.palette.success.main,
                }}
                path={mdiCardAccountDetails}
                size={1}
              />

              <ProtectedElement source="licensePlate">
                <TextField
                  source="licensePlate"
                  label="resources.admin-trailers.labels.licencePlateNumber"
                />
              </ProtectedElement>
            </div>
          </Tooltip>
        ) : null}
        {record && record.ownership ? (
          <Tooltip title="Ownership">
            <div
              style={{
                display: "flex",
                gap: "6px",
                alignItems: "center",
              }}
            >
              <HomeWorkIcon
                style={{
                  color: theme.palette.success.main,
                }}
              />
              <ProtectedElement source="ownership">
                <FunctionField<ITrailerGridView & Record>
                  source="ownership"
                  label="resources.admin-trailers.labels.ownership"
                  render={(record) =>
                    record?.ownership
                      ? translate(`appData.ownershipTypes.${record?.ownership}`)
                      : null
                  }
                />
              </ProtectedElement>
            </div>
          </Tooltip>
        ) : null}
        {record && record.suspension ? (
          <Tooltip title="Suspension">
            <div
              style={{
                display: "flex",
                gap: "6px",
                alignItems: "center",
              }}
            >
              <BlockIcon
                style={{
                  color: theme.palette.success.main,
                }}
              />
              <ProtectedElement source="suspension">
                <FunctionField<ITrailerGridView & Record>
                  source="suspension"
                  label="resources.admin-trailers.labels.suspension"
                  render={(record) =>
                    record?.suspension
                      ? translate(
                          `appData.trailerSuspensionTypes.${record?.suspension}`
                        )
                      : null
                  }
                />
              </ProtectedElement>
            </div>
          </Tooltip>
        ) : null}
        <Tooltip title="Document names">
          <div
            style={{
              display: "flex",
              gap: "6px",
              alignItems: "center",
            }}
          >
            <Icon
              style={{
                color: theme.palette.success.main,
              }}
              path={mdiFileDocumentOutline}
              size={1}
            />
            <ProtectedElement source="documentNames">
              <ArrayField
                record={record}
                source="documentNames"
                label="resources.common.labels.documents"
                sortable={false}
                displayedElementsCount={1}
                emptyArrayDisplay="No documents"
              />
            </ProtectedElement>
          </div>
        </Tooltip>
      </div>
    );
  };

  return (
    <div className="content-container">
      <FieldPermissionsContext.Provider value={fieldPermissions}>
        <DefaultProLogsList
          empty={<ProLogsEmpty iconType={TrailerOutlineIcon} />}
          className="content-list"
          title="resources.admin-trailers.name"
          bulkActionButtons={false}
          actions={<ListActions />}
          filters={<ListFilter />}
          sort={{ field: "name", order: "ASC" }}
          {...props}
        >
          <Responsive
            small={
              <SimpleList
                className="simple-list-wrapper"
                linkType="edit"
                primaryText={(record) => (
                  <RenderPrimaryTextForSimpleList record={record} />
                )}
                secondaryText={(record) => (
                  <RenderSecondaryTextForSimpleList record={record} />
                )}
                rightIcon={(record) => {
                  return (
                    <ActionsField
                      record={record}
                      {...props}
                      hasShow={props.hasShow}
                      editable={() =>
                        permissions &&
                        permissions[permissionNames.admin.trailers.main] &&
                        permissions[permissionNames.admin.trailers.update]
                      }
                      canActivateOrDeactivate={() =>
                        permissions &&
                        permissions[permissionNames.admin.trailers.main] &&
                        permissions[permissionNames.admin.trailers.update]
                      }
                    >
                      {permissions &&
                        permissions[permissionNames.admin.trailers.main] &&
                        permissions[permissionNames.admin.trailers.delete] && (
                          <DeleteRecordMenuItem />
                        )}
                    </ActionsField>
                  );
                }}
              />
            }
            medium={
              <Datagrid>
                <ProtectedElement source="name">
                  <TextField
                    source="name"
                    label="resources.admin-trailers.labels.name"
                  />
                </ProtectedElement>
                <ProtectedElement source="trailerType">
                  <FunctionField<ITrailerGridView & Record>
                    source="trailerType"
                    label="resources.admin-trailers.labels.type"
                    render={(record) =>
                      translate(`appData.trailerTypes.${record?.trailerType}`)
                    }
                  />
                </ProtectedElement>
                <ProtectedElement source="make">
                  <TextField
                    source="make"
                    label="resources.admin-trailers.labels.make"
                  />
                </ProtectedElement>
                <ProtectedElement source="year">
                  <TextField
                    source="year"
                    label="resources.admin-trailers.labels.year"
                  />
                </ProtectedElement>
                <ProtectedElement source="licensePlate">
                  <TextField
                    source="licensePlate"
                    label="resources.admin-trailers.labels.licencePlate"
                  />
                </ProtectedElement>
                <ProtectedElement source="ownership">
                  <FunctionField<ITrailerGridView & Record>
                    source="ownership"
                    label="resources.admin-trailers.labels.ownership"
                    render={(record) =>
                      record?.ownership
                        ? translate(
                            `appData.ownershipTypes.${record?.ownership}`
                          )
                        : null
                    }
                  />
                </ProtectedElement>
                <ProtectedElement source="suspension">
                  <FunctionField<ITrailerGridView & Record>
                    source="suspension"
                    label="resources.admin-trailers.labels.suspension"
                    render={(record) =>
                      record?.suspension
                        ? translate(
                            `appData.trailerSuspensionTypes.${record?.suspension}`
                          )
                        : null
                    }
                  />
                </ProtectedElement>
                <ProtectedElement source="documentNames">
                  <ArrayField
                    source="documentNames"
                    label="resources.common.labels.documents"
                    sortable={false}
                    displayedElementsCount={1}
                    emptyArrayDisplay="No documents"
                  />
                </ProtectedElement>
                <FunctionField<ITrailerGridView & Record>
                  source="status"
                  headerClassName="centered-content"
                  label="resources.common.labels.active"
                  render={(record) => (
                    <Box className="centered-content">
                      {record?.status === "Active" ? (
                        <CheckBoxIcon
                          className={record.status}
                          style={{
                            color:
                              themeName === "dark"
                                ? theme.palette.success.dark
                                : theme.palette.success.light,
                          }}
                        />
                      ) : (
                        <IndeterminateCheckBoxIcon
                          className={record?.status}
                          style={{
                            color:
                              themeName === "dark"
                                ? theme.palette.error.dark
                                : theme.palette.error.light,
                          }}
                        />
                      )}
                    </Box>
                  )}
                />
                <ActionsField
                  hasShow={props.hasShow}
                  editable={() =>
                    permissions &&
                    permissions[permissionNames.admin.trailers.main] &&
                    permissions[permissionNames.admin.trailers.update]
                  }
                  canActivateOrDeactivate={() =>
                    permissions &&
                    permissions[permissionNames.admin.trailers.main] &&
                    permissions[permissionNames.admin.trailers.update]
                  }
                >
                  {permissions &&
                    permissions[permissionNames.admin.trailers.main] &&
                    permissions[permissionNames.admin.trailers.delete] && (
                      <DeleteRecordMenuItem />
                    )}
                </ActionsField>
              </Datagrid>
            }
          />
        </DefaultProLogsList>
      </FieldPermissionsContext.Provider>
    </div>
  );
};
